
import React, { useState, useMemo } from 'react';
import { Group, User, Expense, Category } from '../types';
import { Users, Plus, Settings, X, Check, Trash2, ArrowLeft, Receipt, PieChart, UserPlus, Copy, Crown, LogOut, Wallet, Mail } from 'lucide-react';
import { ExpenseList } from './ExpenseList';
import { storageService } from '../services/storageService';

interface GroupsProps {
  groups: Group[];
  currentUser: User;
  allUsers: User[];
  expenses: Expense[]; // Historical (Real) Data for balances
  monthlyExpenses: Expense[]; // Projected monthly data
  selectedDate: Date;
  onUpdateGroups: (groups: Group[]) => void;
  // We need categories for ExpenseList reuse
  categories?: Category[]; 
  onToggleStatus?: (expense: Expense) => void;
}

type GroupTab = 'overview' | 'expenses' | 'members' | 'settings';

export const Groups: React.FC<GroupsProps> = ({ groups, currentUser, allUsers, expenses, monthlyExpenses, selectedDate, onUpdateGroups, categories = [], onToggleStatus }) => {
  
  // Navigation State
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<GroupTab>('overview');

  // Logic for Create/Edit Modal
  const [showModal, setShowModal] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  
  // Form State
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formMembers, setFormMembers] = useState<string[]>([]);
  const [copiedCode, setCopiedCode] = useState(false);

  // Email Invite State
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteStatus, setInviteStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const activeGroup = useMemo(() => groups?.find(g => g.id === activeGroupId), [groups, activeGroupId]);

  // --- Calculations ---

  const calculateBalances = (group: Group) => {
    const groupExpenses = expenses.filter(e => e.groupId === group.id && e.splitEqually && e.type === 'expense');
    
    const ledger: Record<string, number> = {};
    group.members?.forEach(m => ledger[m.id] = 0);

    groupExpenses.forEach(expense => {
      const payerId = expense.paidByUserId;
      const amount = expense.amount;
      const splitAmount = amount / (group.members?.length || 1);

      if (ledger[payerId] !== undefined) ledger[payerId] += amount;
      
      group.members?.forEach(member => {
        if (ledger[member.id] !== undefined) ledger[member.id] -= splitAmount;
      });
    });

    return ledger;
  };

  const getSimplifiedDebts = (ledger: Record<string, number>) => {
    const debts: {from: string, to: string, amount: number}[] = [];
    const debtors = Object.entries(ledger).filter(([, val]) => val < -0.01).sort((a, b) => a[1] - b[1]); 
    const creditors = Object.entries(ledger).filter(([, val]) => val > 0.01).sort((a, b) => b[1] - a[1]); 

    let i = 0; 
    let j = 0; 

    while (i < debtors.length && j < creditors.length) {
      const debtor = debtors[i];
      const creditor = creditors[j];
      
      const amount = Math.min(Math.abs(debtor[1]), creditor[1]);
      
      debts.push({
        from: debtor[0],
        to: creditor[0],
        amount: amount
      });

      debtor[1] += amount;
      creditor[1] -= amount;

      if (Math.abs(debtor[1]) < 0.01) i++;
      if (creditor[1] < 0.01) j++;
    }

    return debts;
  };

  // --- Handlers ---

  const handleOpenModal = (group?: Group) => {
    if (group) {
      setEditingGroupId(group.id);
      setFormName(group.name);
      setFormDesc(group.description || '');
      setFormMembers(group.members?.map(m => m.id) || []);
    } else {
      setEditingGroupId(null);
      setFormName('');
      setFormDesc('');
      setFormMembers([currentUser.id]);
    }
    setShowModal(true);
  };

  const handleToggleMember = (userId: string) => {
    if (userId === currentUser.id) return; 
    setFormMembers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const handleSaveGroup = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;

    const resolvedMembers = allUsers?.filter(u => formMembers.includes(u.id)) || [];

    if (editingGroupId) {
      const updatedGroups = groups.map(g => 
        g.id === editingGroupId ? { ...g, name: formName, description: formDesc, members: resolvedMembers } : g
      );
      onUpdateGroups(updatedGroups);
    } else {
      const newGroup: Group = {
        id: Math.random().toString(36).substr(2, 9),
        name: formName,
        description: formDesc,
        members: resolvedMembers,
        createdBy: currentUser.id 
      };
      onUpdateGroups([...groups, newGroup]);
    }
    setShowModal(false);
  };

  const handleDeleteGroup = (groupId: string) => {
    if(confirm("Tem certeza que deseja excluir este grupo?")) {
      onUpdateGroups(groups.filter(g => g.id !== groupId));
      setActiveGroupId(null); 
    }
  };

  const handleLeaveGroup = (groupId: string) => {
    if(confirm("Deseja sair deste grupo?")) {
       const group = groups.find(g => g.id === groupId);
       if(group) {
         const updatedMembers = group.members.filter(m => m.id !== currentUser.id);
         const updatedGroups = groups.map(g => g.id === groupId ? {...g, members: updatedMembers} : g);
         onUpdateGroups(updatedGroups);
         setActiveGroupId(null);
       }
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleAddByEmail = async () => {
    if (!inviteEmail.trim() || !activeGroup) return;
    
    setInviteStatus('loading');
    
    try {
      // Find user in DB by email
      const foundUser = await storageService.getUserByEmail(inviteEmail.trim());
      
      if (!foundUser) {
        alert("Usuário não encontrado com este e-mail. Peça para ele se cadastrar no Fiantro primeiro.");
        setInviteStatus('error');
        return;
      }

      if (activeGroup.members.some(m => m.id === foundUser.id)) {
        alert("Este usuário já está no grupo.");
        setInviteStatus('idle');
        return;
      }

      await storageService.addMemberToGroup(activeGroup.id, foundUser.id);
      
      // Update local state immediately
      const updatedMembers = [...activeGroup.members, foundUser];
      const updatedGroups = groups.map(g => g.id === activeGroup.id ? {...g, members: updatedMembers} : g);
      onUpdateGroups(updatedGroups);
      
      setInviteEmail('');
      setInviteStatus('success');
      setTimeout(() => setInviteStatus('idle'), 2000);
    } catch (error) {
      console.error(error);
      setInviteStatus('error');
      alert("Erro ao adicionar membro.");
    }
  };

  // --- Views ---

  // 1. DETAIL VIEW
  if (activeGroupId && activeGroup) {
     const balances = calculateBalances(activeGroup);
     const myBalance = balances[currentUser.id] || 0;
     const debts = getSimplifiedDebts({...balances});
     
     const groupExpensesMonth = expenses.filter(e => 
        e.groupId === activeGroup.id && 
        new Date(e.date).getMonth() === selectedDate.getMonth() &&
        new Date(e.date).getFullYear() === selectedDate.getFullYear()
     );

     const isAdmin = activeGroup.createdBy === currentUser.id;

     return (
       <div className="space-y-6 pb-20">
          {/* Header */}
          <div className="flex items-center gap-4">
             <button onClick={() => setActiveGroupId(null)} className="p-3 bg-white dark:bg-zinc-900 rounded-2xl shadow-sm hover:bg-slate-50 dark:hover:bg-zinc-800 text-slate-600 dark:text-slate-300">
               <ArrowLeft size={20} />
             </button>
             <div>
               <h2 className="text-2xl font-bold text-slate-900 dark:text-white leading-none">{activeGroup.name}</h2>
               <p className="text-slate-400 text-xs font-medium mt-1">{activeGroup.members?.length || 0} membros</p>
             </div>
          </div>

          {/* Tabs */}
          <div className="flex p-1 bg-slate-200/50 dark:bg-zinc-800 rounded-2xl overflow-x-auto">
             {(['overview', 'expenses', 'members', 'settings'] as GroupTab[]).map(tab => (
               <button
                 key={tab}
                 onClick={() => setActiveTab(tab)}
                 className={`flex-1 py-3 px-4 rounded-xl text-xs font-bold uppercase tracking-wide transition-all whitespace-nowrap ${activeTab === tab ? 'bg-white dark:bg-zinc-700 shadow-sm text-brand-dark dark:text-white' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`}
               >
                 {tab === 'overview' ? 'Resumo' : tab === 'expenses' ? 'Extrato' : tab === 'members' ? 'Membros' : 'Config'}
               </button>
             ))}
          </div>

          {/* CONTENT: OVERVIEW */}
          {activeTab === 'overview' && (
             <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                {/* My Balance Card */}
                <div className="bg-brand-dark dark:bg-zinc-900 text-white p-6 rounded-[2rem] relative overflow-hidden">
                   <div className="absolute top-0 right-0 w-32 h-32 bg-brand-lime opacity-10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"></div>
                   <p className="text-slate-400 text-sm font-medium mb-1">Seu saldo no grupo</p>
                   <div className="flex items-baseline gap-2">
                      <h3 className={`text-3xl font-bold ${myBalance >= 0 ? 'text-brand-lime' : 'text-red-400'}`}>
                         {myBalance >= 0 ? '+' : ''} R$ {myBalance.toFixed(2)}
                      </h3>
                   </div>
                   <p className="text-xs text-slate-500 mt-2">
                      {myBalance > 0 ? 'Você tem a receber' : myBalance < 0 ? 'Você deve pagar' : 'Tudo quitado'}
                   </p>
                </div>

                {/* Who owes who */}
                <div>
                   <h3 className="font-bold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                     <Wallet size={18} className="text-slate-400" /> Saldos Pendentes
                   </h3>
                   {debts?.length === 0 ? (
                     <div className="text-center py-8 bg-white dark:bg-zinc-900 rounded-3xl border border-dashed border-slate-200 dark:border-zinc-800">
                        <Check size={24} className="mx-auto text-emerald-500 mb-2" />
                        <p className="text-sm text-slate-500 dark:text-slate-400">Ninguém deve nada a ninguém.</p>
                     </div>
                   ) : (
                     <div className="space-y-3">
                        {debts?.map((debt, idx) => {
                           const fromUser = allUsers?.find(u => u.id === debt.from)?.name || 'Desconhecido';
                           const toUser = allUsers?.find(u => u.id === debt.to)?.name || 'Desconhecido';
                           const isMePayer = debt.from === currentUser.id;
                           const isMeReceiver = debt.to === currentUser.id;

                           return (
                             <div key={idx} className="bg-white dark:bg-zinc-900 p-4 rounded-2xl shadow-sm border border-slate-100 dark:border-zinc-800 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                   <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center font-bold text-xs text-slate-500 dark:text-slate-400">
                                      {fromUser.substring(0,2)}
                                   </div>
                                   <div className="flex flex-col">
                                      <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                                         {isMePayer ? 'Você' : fromUser}
                                      </span>
                                      <span className="text-[10px] text-slate-400 uppercase font-bold">deve para</span>
                                      <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                                         {isMeReceiver ? 'Você' : toUser}
                                      </span>
                                   </div>
                                </div>
                                <span className="font-bold text-slate-800 dark:text-white">R$ {debt.amount.toFixed(2)}</span>
                             </div>
                           )
                        })}
                     </div>
                   )}
                </div>
             </div>
          )}

          {/* CONTENT: EXPENSES */}
          {activeTab === 'expenses' && (
             <div className="animate-in fade-in">
                <ExpenseList 
                   expenses={groupExpensesMonth}
                   categories={categories}
                   groups={groups}
                   selectedDate={selectedDate}
                   onToggleStatus={onToggleStatus}
                   allUsers={allUsers}
                />
             </div>
          )}

          {/* CONTENT: MEMBERS */}
          {activeTab === 'members' && (
             <div className="space-y-6 animate-in fade-in">
                
                {/* Invite Email Section */}
                <div className="bg-white dark:bg-zinc-900 p-6 rounded-[2rem] shadow-sm">
                   <h3 className="font-bold text-slate-800 dark:text-white mb-3 text-sm flex items-center gap-2">
                     <UserPlus size={16} className="text-brand-lime" /> Adicionar Membro
                   </h3>
                   <div className="flex gap-2">
                      <div className="relative flex-1">
                         <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                         <input 
                           type="email" 
                           placeholder="E-mail do usuário"
                           value={inviteEmail}
                           onChange={(e) => setInviteEmail(e.target.value)}
                           className="w-full pl-9 pr-4 py-3 bg-slate-50 dark:bg-zinc-800 border-none rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-lime dark:text-white placeholder:text-slate-400"
                         />
                      </div>
                      <button 
                        onClick={handleAddByEmail}
                        disabled={inviteStatus === 'loading'}
                        className="bg-brand-dark dark:bg-white text-white dark:text-black px-4 rounded-xl font-bold text-xs hover:opacity-90 transition-opacity disabled:opacity-50"
                      >
                        {inviteStatus === 'loading' ? '...' : inviteStatus === 'success' ? <Check size={16} /> : 'Adicionar'}
                      </button>
                   </div>
                </div>

                {/* Invite Code */}
                {activeGroup.inviteCode && (
                  <div className="bg-brand-lime/10 border border-brand-lime/20 p-6 rounded-[2rem] text-center">
                     <p className="text-brand-dark font-bold text-sm uppercase tracking-wide mb-2">Código de Convite</p>
                     <div 
                        onClick={() => handleCopyCode(activeGroup.inviteCode!)}
                        className="text-3xl font-mono font-bold text-brand-dark tracking-widest cursor-pointer hover:scale-105 transition-transform active:scale-95"
                     >
                        {activeGroup.inviteCode}
                     </div>
                     <p className="text-xs text-slate-500 mt-2 flex items-center justify-center gap-1">
                        {copiedCode ? <Check size={12} className="text-green-600"/> : <Copy size={12} />}
                        {copiedCode ? 'Copiado!' : 'Toque para copiar'}
                     </p>
                  </div>
                )}

                <div className="space-y-3">
                   {activeGroup.members?.map(member => {
                      const isOwner = member.id === activeGroup.createdBy;
                      const isMe = member.id === currentUser.id;

                      return (
                        <div key={member.id} className="bg-white dark:bg-zinc-900 p-4 rounded-2xl flex items-center justify-between shadow-sm">
                           <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-full bg-slate-200 dark:bg-zinc-800 overflow-hidden">
                                 <img src={member.avatarUrl || `https://ui-avatars.com/api/?name=${member.name}`} className="w-full h-full object-cover" />
                              </div>
                              <div>
                                 <h4 className="font-bold text-slate-800 dark:text-white">{member.name} {isMe && '(Você)'}</h4>
                                 <p className="text-xs text-slate-400">{member.email}</p>
                              </div>
                           </div>
                           <div className="flex items-center gap-2">
                              {isOwner && (
                                 <span className="bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 px-2 py-1 rounded-lg text-[10px] font-bold uppercase flex items-center gap-1">
                                    <Crown size={10} /> Dono
                                 </span>
                              )}
                              {isAdmin && !isMe && (
                                 <button className="p-2 text-slate-300 hover:text-red-500 bg-slate-50 hover:bg-red-50 dark:bg-zinc-800 dark:hover:bg-red-500/20 rounded-xl transition-colors">
                                    <Trash2 size={16} />
                                 </button>
                              )}
                           </div>
                        </div>
                      );
                   })}
                </div>
             </div>
          )}

          {/* CONTENT: SETTINGS */}
          {activeTab === 'settings' && (
             <div className="space-y-6 animate-in fade-in">
                <div className="bg-white dark:bg-zinc-900 p-6 rounded-[2rem] shadow-sm">
                   <h3 className="font-bold text-slate-800 dark:text-white mb-4">Editar Grupo</h3>
                   <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Nome</label>
                        <input 
                           type="text" 
                           value={activeGroup.name} 
                           readOnly
                           className="w-full px-4 py-3 bg-slate-50 dark:bg-zinc-800 rounded-xl text-slate-500" 
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Para editar, use o botão abaixo.</p>
                      </div>
                      
                      <button 
                        onClick={() => handleOpenModal(activeGroup)}
                        className="w-full py-3 bg-brand-dark dark:bg-white text-white dark:text-black rounded-xl font-bold text-sm"
                      >
                        Editar Informações
                      </button>
                   </div>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-6 rounded-[2rem] shadow-sm">
                   <h3 className="font-bold text-red-500 mb-4">Zona de Perigo</h3>
                   <div className="space-y-3">
                      {isAdmin ? (
                        <button 
                           onClick={() => handleDeleteGroup(activeGroup.id)}
                           className="w-full py-4 bg-red-50 dark:bg-red-500/10 text-red-500 dark:text-red-400 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-red-100 dark:hover:bg-red-500/20"
                        >
                           <Trash2 size={18} /> Excluir Grupo Permanentemente
                        </button>
                      ) : (
                        <button 
                           onClick={() => handleLeaveGroup(activeGroup.id)}
                           className="w-full py-4 bg-red-50 dark:bg-red-500/10 text-red-500 dark:text-red-400 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-red-100 dark:hover:bg-red-500/20"
                        >
                           <LogOut size={18} /> Sair do Grupo
                        </button>
                      )}
                   </div>
                </div>
             </div>
          )}
       </div>
     );
  }

  // 2. LIST VIEW (Default)
  return (
    <div className="space-y-6 pb-20">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Grupos</h2>
          <p className="text-slate-400 text-sm mt-1">Seus espaços compartilhados</p>
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="w-12 h-12 bg-brand-black dark:bg-zinc-900 text-white rounded-2xl flex items-center justify-center hover:bg-slate-800 dark:hover:bg-zinc-800 transition-colors shadow-lg shadow-slate-200 dark:shadow-none"
        >
          <Plus size={24} />
        </button>
      </div>

      {groups?.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-zinc-900 rounded-[2rem] border border-dashed border-slate-200 dark:border-zinc-800">
           <div className="w-16 h-16 bg-slate-50 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
             <Users size={32} />
           </div>
           <p className="text-slate-500 font-medium">Você ainda não tem grupos.</p>
           <button onClick={() => handleOpenModal()} className="mt-4 text-brand-lime bg-brand-dark px-4 py-2 rounded-xl text-sm font-bold">
             Criar Primeiro Grupo
           </button>
        </div>
      ) : (
        <div className="grid gap-6">
          {groups?.map(group => {
            const balances = calculateBalances(group);
            const myBalance = balances[currentUser.id] || 0;
            
            // Total spent by group in selected month
            const totalGroupSpendMonth = monthlyExpenses.filter(e => 
              e.groupId === group.id && e.type === 'expense'
            ).reduce((acc, cur) => acc + cur.amount, 0);

            return (
              <div 
                key={group.id} 
                onClick={() => setActiveGroupId(group.id)}
                className="bg-white dark:bg-zinc-900 rounded-[2rem] p-6 shadow-soft relative overflow-hidden hover:shadow-lg transition-all cursor-pointer group-card"
              >
                {/* Decorative gradient */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 dark:bg-zinc-800 rounded-bl-[100px] -z-0"></div>

                <div className="relative z-10">
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-brand-lime text-brand-dark rounded-2xl flex items-center justify-center shadow-glow">
                        <Users size={28} />
                      </div>
                      <div>
                        <h3 className="font-bold text-xl text-slate-800 dark:text-white">{group.name}</h3>
                        <p className="text-xs text-slate-400 font-medium uppercase tracking-wide mt-1">{group.members?.length || 0} MEMBROS</p>
                      </div>
                    </div>
                    {/* Month Total */}
                    <div className="text-right">
                        <p className="text-xs text-slate-400 mb-1 capitalize">{selectedDate.toLocaleString('pt-BR', { month: 'short' })}</p>
                        <p className="font-bold text-slate-800 dark:text-white text-lg">R$ {totalGroupSpendMonth.toFixed(0)}</p>
                    </div>
                  </div>

                  <div className="bg-slate-50 dark:bg-zinc-800 rounded-2xl p-4 mb-4 flex items-center justify-between border border-slate-100 dark:border-zinc-700">
                      <span className="text-sm text-slate-500 font-medium">Seu saldo geral</span>
                      <span className={`text-lg font-bold ${myBalance > 0.01 ? 'text-brand-lime px-2 py-1 bg-brand-dark rounded-lg' : myBalance < -0.01 ? 'text-red-500' : 'text-slate-400'}`}>
                        {myBalance > 0.01 ? '+' : ''}{myBalance.toFixed(2)}
                      </span>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <div className="flex -space-x-2">
                      {group.members?.slice(0,4).map(member => (
                        <div key={member.id} className="w-8 h-8 rounded-full border-2 border-white dark:border-zinc-900 bg-slate-200 dark:bg-zinc-700 flex items-center justify-center text-[10px] font-bold text-slate-600 dark:text-slate-300 overflow-hidden">
                          {member.avatarUrl ? <img src={member.avatarUrl} className="w-full h-full object-cover" /> : member.name[0]}
                        </div>
                      ))}
                      {(group.members?.length || 0) > 4 && (
                         <div className="w-8 h-8 rounded-full border-2 border-white dark:border-zinc-900 bg-slate-100 dark:bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-slate-400">
                           +{(group.members?.length || 0) - 4}
                         </div>
                      )}
                    </div>
                    <span className="text-xs font-bold text-brand-dark dark:text-brand-lime flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                       Ver detalhes <ArrowLeft size={12} className="rotate-180" />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-zinc-900 w-full max-w-sm rounded-[2rem] p-6 animate-in zoom-in-95 duration-200 shadow-2xl max-h-[90vh] overflow-y-auto">
             <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                  {editingGroupId ? 'Editar Grupo' : 'Novo Grupo'}
                </h3>
                <button onClick={() => setShowModal(false)} className="p-2 bg-slate-100 dark:bg-zinc-800 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-700 text-slate-500 dark:text-slate-400">
                  <X size={20} />
                </button>
             </div>

             <form onSubmit={handleSaveGroup} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5">Nome do Grupo</label>
                  <input 
                    type="text" 
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-zinc-800 border-none rounded-xl font-medium text-slate-800 dark:text-white focus:ring-2 focus:ring-brand-lime outline-none"
                    placeholder="Ex: Viagem"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5">Descrição</label>
                  <input 
                    type="text" 
                    value={formDesc}
                    onChange={(e) => setFormDesc(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-zinc-800 border-none rounded-xl font-medium text-slate-800 dark:text-white focus:ring-2 focus:ring-brand-lime outline-none"
                    placeholder="Ex: Custos do feriado"
                  />
                </div>

                <div>
                   <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Membros</label>
                   <div className="space-y-2 bg-slate-50 dark:bg-zinc-800 p-2 rounded-xl max-h-40 overflow-y-auto">
                      {allUsers?.map(user => {
                        const isSelected = formMembers.includes(user.id);
                        const isMe = user.id === currentUser.id;

                        return (
                          <div 
                            key={user.id} 
                            onClick={() => handleToggleMember(user.id)}
                            className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-white dark:bg-zinc-700 shadow-sm' : 'hover:bg-slate-200 dark:hover:bg-zinc-700'}`}
                          >
                             <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-zinc-600 overflow-hidden">
                                   {user.avatarUrl ? <img src={user.avatarUrl} className="w-full h-full" /> : <span className="flex items-center justify-center h-full text-xs font-bold text-slate-600 dark:text-slate-300">{user.name[0]}</span>}
                                </div>
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{user.name} {isMe && '(Você)'}</span>
                             </div>
                             {isSelected && <div className="text-brand-lime bg-brand-dark rounded-full p-1"><Check size={12} /></div>}
                          </div>
                        );
                      })}
                   </div>
                </div>

                <div className="pt-2 gap-2 flex flex-col">
                  <button 
                    type="submit" 
                    className="w-full bg-brand-dark dark:bg-white text-brand-lime dark:text-black py-4 rounded-xl font-bold text-lg hover:shadow-lg"
                  >
                    Salvar Grupo
                  </button>
                  
                  {editingGroupId && (
                     <button 
                       type="button"
                       onClick={() => handleDeleteGroup(editingGroupId)}
                       className="w-full bg-red-50 dark:bg-red-500/10 text-red-500 dark:text-red-400 py-3 rounded-xl font-bold text-sm hover:bg-red-100 dark:hover:bg-red-500/20 flex items-center justify-center gap-2"
                     >
                       <Trash2 size={16} /> Excluir Grupo
                     </button>
                  )}
                </div>
             </form>
          </div>
        </div>
      )}
    </div>
  );
};
